import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import * as React from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { PlayerPhoto, PlayerStanding } from '../api/leaderboard';
import { ReactComponent as RacketIcon } from '../images/squash-racket.svg';

type Props = {
  playerStandings: PlayerStanding[];
  playerPhotos: PlayerPhoto[];
  podiumCount?: number;
};

const Leaderboard = ({ playerStandings, playerPhotos, podiumCount = 3 }: Props) => {
  const sortedPlayerStandings = playerStandings.sort((a, b) => b.eloScore - a.eloScore);

  const podiumPlayers = sortedPlayerStandings.slice(0, podiumCount);
  const remainingPlayers = sortedPlayerStandings.slice(podiumCount);

  return (
    <Container>
      <Podium>
        <PodiumTitleContainer>
          <StyledRacketIcon />
          <PodiumTitle>
            <LeaderboardHeading>
              <span>Live</span>
              Leaderboard
            </LeaderboardHeading>
          </PodiumTitle>
        </PodiumTitleContainer>
        {podiumPlayers.map((player, index) => {
          const playerPhoto = playerPhotos.find((photo) => photo.id === player.id);

          return (
            <PodiumPlayerContainer key={player.id} layout>
              <h3>{index + 1}</h3>
              <PodiumPlayerInfo>
                <h4>{formatName(player.name)}</h4>
                <PodiumPlayerImageSlot>
                  <PlayerImageContainer layoutId={`player/${player.id.toString()}/photo`}>
                    {playerPhoto && (
                      <motion.img
                        alt={formatName(player.name)}
                        src={playerPhoto.url}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1 }}
                      />
                    )}
                  </PlayerImageContainer>
                </PodiumPlayerImageSlot>
                <span>{player.eloScore.toLocaleString()}</span>
              </PodiumPlayerInfo>
            </PodiumPlayerContainer>
          );
        })}
      </Podium>
      {remainingPlayers.length > 0 && (
        <PlayerContainer
          variants={{
            hidden: { opacity: 0 },
            show: {
              opacity: 1,
              transition: {
                staggerChildren: 0.04,
              },
            },
          }}
          layout
          initial="hidden"
          animate="show"
        >
          {remainingPlayers.map((player, index) => {
            const playerPhoto = playerPhotos.find((photo) => photo.id === player.id);

            return (
              <PlayerCardContainer
                key={player.id}
                variants={{
                  hidden: { y: 40, opacity: 0 },
                  show: { y: 0, opacity: 1 },
                }}
                layout
              >
                <PlayerCardRank>{index + podiumCount + 1}</PlayerCardRank>
                <PlayerCardImageSlot>
                  <PlayerCardImageContainer layoutId={`player/${player.id.toString()}/photo`}>
                    {playerPhoto && (
                      <motion.img
                        alt={player.name}
                        src={playerPhoto.url}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1 }}
                      />
                    )}
                  </PlayerCardImageContainer>
                </PlayerCardImageSlot>
                <PlayerCardName>{formatName(player.name)}</PlayerCardName>
                <PlayerCardEloScore>{player.eloScore.toLocaleString()}</PlayerCardEloScore>
              </PlayerCardContainer>
            );
          })}
        </PlayerContainer>
      )}
    </Container>
  );
};

const formatName = (fullName: string) => {
  const [firstName, ...rest] = fullName.split(' ');
  const lastName = rest[rest.length - 1];

  if (!lastName) {
    return firstName;
  }

  const [lastNameInitial] = lastName;

  // First name + First initial of last name
  return [firstName, lastNameInitial].join(' ');
};

const LeaderboardHeading = styled.h2`
  display: flex;
  flex-direction: column;
  margin: 0;
  font-size: 2rem;

  @media (min-width: 525px) {
    font-size: 2.5rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.5rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: 2.5rem;
  }

  span {
    margin-right: 0.5rem;
    font-weight: 300;
    margin-right: 0.5rem;
    font-size: 2rem;

    @media (min-width: 525px) {
      font-size: 2.5rem;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: 1.5rem;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  font-family: 'Nunito', serif;
  margin-bottom: 6rem;
`;

const StyledRacketIcon = styled(RacketIcon)`
  width: 2rem;
  height: 3rem;
  margin-right: 0.5rem;
`;

const Podium = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  color: #ffffff;
  background-image: linear-gradient(90deg, #eb1b2e 0%, var(--purple-color) 100%);
  border-radius: 20px;
  padding: 1.5rem 1.75rem 2rem 1.75rem;
  margin-bottom: 1.5rem;

  @media (min-width: 525px) {
    padding: 2rem 3rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    padding: 2rem;
  }
`;

const PodiumTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-top: 1rem;
  margin-bottom: 2rem;

  @media (min-width: 525px) {
    margin-top: 1.5rem;
    margin-bottom: 3rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 0;
  }
`;

const PodiumTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  @media (min-width: 525px) {
    flex-direction: row;
    align-items: center;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PodiumPlayerContainer = styled(motion.div)`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  margin-bottom: 1.5rem;

  :last-of-type {
    margin-bottom: 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    width: auto;
    justify-content: flex-start;
    margin: 0;
  }

  h3 {
    flex: 1;
    opacity: 0.5;
    font-weight: 600;
    font-family: 'Lato', serif;
    margin: 0;
    font-size: 1.5rem;

    @media (min-width: 525px) {
      font-size: 2rem;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      margin-right: 0.75rem;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      font-size: 4.5rem;
      margin-right: 1.5rem;
    }
  }
`;

const PodiumPlayerInfo = styled.div`
  flex: 6;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }

  h4 {
    order: 2;
    flex: 3;
    margin: 0;
    font-size: 1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (min-width: 525px) {
      font-size: 1.5rem;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      order: 1;
      flex-direction: column;
      margin-bottom: 1.45rem;
      font-size: 1rem;
    }
  }

  span {
    order: 3;
    flex: 1;
    font-weight: 700;
    font-style: italic;
    margin: 0;
    font-size: 1rem;

    @media (min-width: 525px) {
      font-size: 1.25rem;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: 1.125rem;
      margin-top: 1rem;
    }
  }
`;

const PodiumPlayerImageSlot = styled.div`
  flex: 2;
  order: 1;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    order: 2;
  }
`;

const PlayerImageContainer = styled(motion.div)`
  width: 3rem;
  height: 3rem;
  border-radius: 1.5rem;
  border: 0.125rem solid #ffffff;
  background-color: #ddd;
  overflow: hidden;

  @media (min-width: 525px) {
    width: 4rem;
    height: 4rem;
    border-radius: 2rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 6rem;
    height: 6rem;
    border-radius: 3rem;
  }
`;

const PlayerContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 0.375rem;
  row-gap: 0.625rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const PlayerCardContainer = styled(motion.div)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  opacity: 0.88;
  background: #f1f2f7;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.21);
  border-radius: 20px;
  padding: 0.5rem 1.75rem;

  @media (min-width: 525px) {
    padding: 0.5rem 3rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    :nth-of-type(odd) {
      border-radius: 20px 0 0 20px;
    }

    :nth-of-type(even) {
      border-radius: 0 20px 20px 0;
    }
  }

  h5 {
    margin: 0;
  }
`;

const PlayerCardRank = styled.h5`
  flex: 1;
  font-weight: 700;
  font-size: 1.25rem;

  @media (min-width: 525px) {
    font-size: 1.5rem;
  }
`;

const PlayerCardImageSlot = styled.div`
  flex: 2;
`;

const PlayerCardImageContainer = styled(PlayerImageContainer)`
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 4rem;
    height: 4rem;
    border-radius: 2rem;
  }
`;

const PlayerCardName = styled.h5`
  flex: 3;
  font-weight: 700;
  color: #4a4a4a;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 525px) {
    font-size: 1.5rem;
  }
`;

const PlayerCardEloScore = styled.span`
  flex: 1;
  font-weight: 400;
  font-style: italic;
  font-size: 1rem;

  @media (min-width: 525px) {
    font-size: 1.25rem;
  }
`;

export default Leaderboard;
